/**
 * Jurisdiction Card
 * =================
 * Displays declared country vs IP-detected location with mismatch warnings.
 * Shows VPN/Proxy/Tor detection status.
 */

import React from 'react';
import {
  Globe,
  MapPin,
  Shield,
  AlertTriangle,
  CheckCircle,
  Wifi,
  Eye,
} from 'lucide-react';

// Country flag emoji helper (ISO 3166-1 alpha-2 to emoji)
function getCountryFlag(countryCode) {
  if (!countryCode || countryCode.length !== 2) return null;
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

// Risk level badge
function RiskLevelBadge({ risk }) {
  const config = risk >= 50
    ? { bg: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', label: 'High' }
    : risk >= 30
    ? { bg: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', label: 'Medium' }
    : { bg: 'rgba(16, 185, 129, 0.15)', color: '#10b981', label: 'Low' };

  return (
    <span style={{
      fontSize: '10px',
      padding: '2px 6px',
      borderRadius: '4px',
      background: config.bg,
      color: config.color,
      fontWeight: 600,
      textTransform: 'uppercase',
    }}>
      {config.label}
    </span>
  );
}

// Status indicator
function StatusIndicator({ active, label, Icon, warning = false }) {
  const color = active
    ? (warning ? '#ef4444' : '#f59e0b')
    : '#10b981';

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '12px',
      color: active ? color : 'var(--text-secondary)',
    }}>
      {active ? (
        <AlertTriangle size={12} style={{ color }} />
      ) : (
        <CheckCircle size={12} style={{ color: '#10b981' }} />
      )}
      <span>{active ? label : `No ${label.toLowerCase()}`}</span>
    </div>
  );
}

export function JurisdictionCard({ jurisdiction = {} }) {
  const {
    declared_country,
    declared_country_name,
    declared_country_risk = 0,
    ip_country,
    ip_country_name,
    ip_country_risk = 0,
    ip_city,
    // ip_region reserved for future use
    mismatch_detected = false,
    vpn_detected = false,
    proxy_detected = false,
    tor_detected = false,
  } = jurisdiction;

  const hasIpData = !!ip_country;

  return (
    <div style={{
      background: 'var(--bg-secondary)',
      borderRadius: '8px',
      padding: '16px',
      border: mismatch_detected
        ? '1px solid rgba(239, 68, 68, 0.3)'
        : '1px solid var(--border-color)',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px',
      }}>
        <Globe size={14} style={{ color: 'var(--accent-primary)' }} />
        <span style={{
          fontSize: '11px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--text-muted)',
        }}>
          Jurisdiction
        </span>
        {mismatch_detected && (
          <span style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '11px',
            fontWeight: 600,
            color: '#ef4444',
          }}>
            <AlertTriangle size={12} />
            Mismatch
          </span>
        )}
      </div>

      {/* Country comparison */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px',
        marginBottom: '12px',
      }}>
        {/* Declared country */}
        <div>
          <div style={{
            fontSize: '10px',
            fontWeight: 500,
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
            marginBottom: '4px',
          }}>
            Declared
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            {declared_country ? (
              <>
                <span style={{ fontSize: '18px' }}>
                  {getCountryFlag(declared_country?.slice(0, 2))}
                </span>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 500 }}>
                    {declared_country_name || declared_country}
                  </div>
                  <RiskLevelBadge risk={declared_country_risk} />
                </div>
              </>
            ) : (
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                Not provided
              </span>
            )}
          </div>
        </div>

        {/* Detected country */}
        <div>
          <div style={{
            fontSize: '10px',
            fontWeight: 500,
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
            marginBottom: '4px',
          }}>
            Detected (IP)
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            {hasIpData ? (
              <>
                <span style={{ fontSize: '18px' }}>
                  {getCountryFlag(ip_country)}
                </span>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 500 }}>
                    {ip_city ? `${ip_city}, ` : ''}{ip_country_name || ip_country}
                  </div>
                  <RiskLevelBadge risk={ip_country_risk} />
                </div>
              </>
            ) : (
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                <MapPin size={12} style={{ display: 'inline', marginRight: '4px' }} />
                No IP data
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Network status indicators */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        paddingTop: '12px',
        borderTop: '1px solid var(--border-color)',
      }}>
        <StatusIndicator
          active={vpn_detected}
          label="VPN"
          Icon={Shield}
          warning={vpn_detected}
        />
        <StatusIndicator
          active={proxy_detected}
          label="Proxy"
          Icon={Wifi}
          warning={proxy_detected}
        />
        <StatusIndicator
          active={tor_detected}
          label="Tor"
          Icon={Eye}
          warning={true}
        />
      </div>
    </div>
  );
}

export default JurisdictionCard;
